import { components } from "@/api-schema";
import { useMailCreateMutation, type MutationBody } from "@/common/api/microsoft-graph/use-mail-create.mutation";
import i18next from "i18next";
import { useCallback } from "react";
import { toast } from "sonner";

type User = components["schemas"]["User"];

const EMAIL_TEMPLATE = {
  content: i18next.t("phonebook:EmailTemplateContent"),
  contentType: "HTML" as const,
};

interface UseCreateDraftMailReturn {
  createDraftMail: (users: User[]) => Promise<void>;
  isPending: boolean;
}

export function useCreateDraftMail(): UseCreateDraftMailReturn {
  const mutation = useMailCreateMutation();

  const createDraftMail = useCallback(
    async (users: User[]): Promise<void> => {
      const usersWithEmail = users.filter((user) => user.email);

      if (usersWithEmail.length === 0) {
        toast.error(i18next.t("phonebook:NoneOfSelectedUsersHaveEmail"));
        return;
      }

      if (usersWithEmail.length !== users.length) {
        toast.warning(i18next.t("phonebook:NotAllSelectedUsersHaveEmail"));
      }

      const emailDraft: MutationBody = {
        body: EMAIL_TEMPLATE,
        subject: i18next.t("phonebook:DraftEmailToEmployees", { count: usersWithEmail.length }),
        toRecipients: usersWithEmail.map((user) => ({
          emailAddress: {
            address: user.email as string,
          },
        })),
      };

      try {
        const response = await mutation.mutateAsync(emailDraft);
        window.open(response.webLink, "_blank");
      } catch (error) {
        console.error("Error creating draft email:", error);
        toast.error(i18next.t("phonebook:CouldNotCreateEmailDraft"));
      }
    },
    [mutation],
  );

  return {
    createDraftMail,
    isPending: mutation.isPending,
  };
}
